import React, { useEffect, useState, useCallback } from "react";
import { OverlayTrigger, Tooltip, Row, Col } from "react-bootstrap";
import { useTranslation, Translation } from "react-i18next";
import { EncryptStorage } from "encrypt-storage";
import { useLocation, useNavigate } from "react-router-dom";
import IconButton from "./common/IconButton";
import * as em from "common/ErrorMessages";
import * as Constants from "common/constants";
import * as General from "common/general";
import UpliftCss from "./common/upliftcss.module.css";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import AdvanceTableWrapper from "components/common/advance-table/AdvanceTableWrapper";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import PageTitle from "./common/PageTitle";
import jwtDecode from "jwt-decode";
import { startWorkflowProcess } from "components/workflow/StartWorkflowProcess";
import { put, getAllDatabaseRecords, refreshWorkflowCounts } from "components/workflow/WorkflowHelper";
import LoaderIcon from "components/custom/Preloader";

const ManageBranches = () => {
    const [companies, setCompanies] = useState([]);
    const [companyID, setCompanyID] = useState();
    const [unn, setUnn] = useState();
    const [companyName, setCompanyName] = useState();
    const [isRTL] = useState(JSON.parse(localStorage.getItem("isRTL")) || false);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [modal, setModal] = useState(false);
    const toggle = () => setModal(!modal);
    const menuName = t("ManageBranches");
    const [companyData, setCompanyData] = useState();
    const [isFinish, setIsFinish] = useState(false);
    const [isHeadQuarter, setIsHeadQuarter] = useState(true)
    const [headQuarter, setHeadQuarter] = useState("")

    const [existMessage, setExistMessage] = useState(null);
    const encryptStorage = new EncryptStorage("secret-key-value", { prefix: "@mwan" });

    const getCompanies = useCallback(async (selectedCompany) => {
        try {
            let loggedInUserInfo = JSON.parse(localStorage.getItem("loggedInUserInfo"));
            setLoading(true);

            // Example API Call or static data
            const companyData = await General.GetBranchesCompany(selectedCompany);

            const company = companyData.map((item) => ({
                companyId: item?.companyId,
                address: item?.address,
                displayName: item?.displayName,
                municipality:item?.municipality!=null?JSON.parse(item?.municipality).label:"",
                action: <>
                    {(!loggedInUserInfo.headQuarter) && <div className="d-flex justify-content-end mb-2 ">
                        <OverlayTrigger
                            overlay={
                                <Tooltip style={{ position: "fixed" }} id="deleteBranch">
                                    {t(em.delete_icon)}
                                </Tooltip>
                            }
                        >
                            <div className="theme-control-toggle-label icon-position">
                                <IconButton
                                    className="btn btn-danger"
                                    iconClassName="fs--3"
                                    variant=""
                                    size="sm"
                                    icon="fa-solid fa-trash"
                                    style={{
                                        margin: "auto 0.25rem",
                                    }}
                                    onClick={() => openModalDelete(item)}
                                ></IconButton>
                            </div>
                        </OverlayTrigger>

                    </div>}
                </>,
            }));

            setCompanies(company);
            setCompanyID(selectedCompany);
        } catch (error) {
            console.error("Error fetching:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const openModalDelete = (company) => {
        setExistMessage("")
        setCompanyData(company)
        setModal(true)

    };


    const handleDelete = async () => {
        setIsFinish(true)
        const selectedCompany = encryptStorage.getItem(Constants.SelectedCompany);
        const getUserId = encryptStorage.getItem("loggedInUserInfo");

        try {
            const [companyUsersData, companyTrucksData, generatorsCompanyData] = await Promise.all([
                General.GetCompanyUsersData(
                    `&&companyId=eq.${companyData.companyId}&&tbl_users.tbl_roles_users.companyId=eq.${companyData.companyId}`
                ),
                General.GetTruckCompany(companyData.companyId),
                General.GetGeneratorsCompany(companyData.companyId),
            ]);

            // Helper function for stopping process with message
            const stopProcess = (message) => {
                setExistMessage(message);
                setIsFinish(false);
            };

            // Early returns for clarity
            if (generatorsCompanyData.length > 0) return stopProcess(em.existingGenerator);
            if (companyTrucksData.length > 0) return stopProcess(em.existingtruck);
            if (companyUsersData.length > 1) return stopProcess(em.existinguser);
            

            // Proceed with unassigning user and patching company
            await General.UnassignUser(getUserId.userId, companyData.companyId, companyData.keycloakCompanyId);

            const companyJSON = {
                language: "en",
                databasePutData: {
                    itemFieldName: "companyId",
                    itemId: companyData.companyId,
                    tableName: "tbl_company",
                    fields: {
                        headQuarter: null,
                        softDeleteDate: new Date(),
                        userDeletedId: getUserId.userId,
                    },
                },
            };

            const companyJSONString = JSON.stringify(companyJSON);

            await General.PatchToEndPoint(
                Constants.PatchEndPoint,
                null,
                companyJSONString,
                (responseObject) => {
                    if (responseObject.status === Constants.SuccessStatus) {
                        setModal(false);
                        getCompanies(selectedCompany);
                        setIsFinish(false);
                    }
                },
                (error) => {
                    console.error(error);
                }
            );
        } catch (error) {
            console.error("Error while processing company removal:", error);
        }
    }

    const getTableData = async (tableName, fieldValue) => {
        try {
            const result = await getAllDatabaseRecords(tableName, fieldValue);
            return result;
        } catch (error) {
            console.error("Error fetching data:", error);
            throw error;
        }
    };

    /**
     * Initialize data when component mounts or refresh triggered
     */
    useEffect(() => {
        const selectedCompany = encryptStorage.getItem(Constants.SelectedCompany);
        let loggedInUserInfo = JSON.parse(localStorage.getItem("loggedInUserInfo"));
        if (loggedInUserInfo.headQuarter != null) {
            setIsHeadQuarter(false)
        }
        if (!selectedCompany) return;

        const parts = selectedCompany.split('-');
        setUnn(parts[parts.length - 1])
        setCompanyName(parts.slice(0, parts.length - 1).join(' '))
        var nameCompanys=loggedInUserInfo?.headQuarter?.split('-');

        setHeadQuarter(nameCompanys!=null?nameCompanys.slice(0, nameCompanys.length - 1).join(' '):parts.slice(0, parts.length - 1).join(' '));

        getCompanies(loggedInUserInfo.headQuarter != null ? loggedInUserInfo.headQuarter : selectedCompany);
    }, [getCompanies,]);

    /**
     * Table Columns
     */
    const columns = [
        {
            accessor: "displayName",
            Header: t("branchName"),
        },
        {
            accessor: "municipality",
            Header: t("municipality"),
        },
        {
             accessor: "address",
            Header: t("Address"),
        },
        {
            accessor: "action",
            Header: t("Action"),
        },
    ];

    /**
     * Start workflow process handler
     */
    const handleStartProcess = async () => {
        try {

             let loggedInUserInfo = JSON.parse(localStorage.getItem("loggedInUserInfo"));
            const initialValue = { unn, companyName, headQuarter: companyID,selectMobileNumber:loggedInUserInfo.mobileNumber };

            const response = await getTableData("tbl_page_title", "page_name=eq." + encodeURIComponent(location.pathname));
            const pageData = response?.data?.dataBaseContent?.[0];

            if (!pageData) throw new Error("Page title info not found.");

            const pageKey = t(pageData.key);

            // then continue with workflow fetch
            const accessToken = encryptStorage.getItem(Constants.AccessTokenKeyInLocalStorage);
            const decodedAccessToken = jwtDecode(accessToken);
            const userId = decodedAccessToken?.sub;

            const workflowUrl = `${Constants.GetWorkflowsEndpoint}?userId=${userId}&companyId=${companyID}`;
            const workflowResponse = await fetch(workflowUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            const newData = await workflowResponse.json();
            const workflowList = Object.values(newData.workflowsResponseList || {});
            const parsedTask = workflowList
                .map((task) => (typeof task === "string" ? JSON.parse(task) : task))
                .find((task) => task?.key === pageKey);

            if (!parsedTask?.id) throw new Error("No matching workflow found for current page key.");

            const formData = await startWorkflowProcess(parsedTask.id, true, initialValue);

            formData.settings = formData.settings || {};
            formData.settings.data = initialValue;

            localStorage.setItem(Constants.CurrentWorkflowStep, JSON.stringify(formData));
            navigate("/form_view_renderer", { state: { data: formData } });
            await refreshWorkflowCounts();
        } catch (error) {
            console.error("Error starting workflow process:", error);
        }
    };


    return (
        <Translation>
            {(t) => (
                <div className="container">
                    <PageTitle menuName={menuName} />

                    <div className={`card ${UpliftCss.BodyBorder}`}>
                        <div className="filterSection mb-3">
                            <div className="invalid-feedback"></div>
                        </div>

                        {isHeadQuarter && <div className="tool-header mb-3">
                            <OverlayTrigger
                                overlay={
                                    <Tooltip style={{ position: "fixed" }} id="newBranch">
                                        {t(em.newBranch)}
                                    </Tooltip>
                                }
                            >
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() =>
                                        handleStartProcess()
                                    }
                                >
                                    {t(em.newBranch)}
                                </button>
                            </OverlayTrigger>
                        </div>}

                        <div className={`card ${UpliftCss.BodyBorder}`}>
                            <div className={`mb-3 ${UpliftCss.tableHeader}`}>
                                <AdvanceTableWrapper columns={columns} data={companies} sortable pagination perPage={10}>
                                    <Row className="flex-end-center mb-3">
                                        <Col xs="auto" sm={6} lg={8}>
                                            <label className={`form-label ${UpliftCss.companyIDLabel}`}>{headQuarter}</label>
                                        </Col>
                                        <Col xs="auto" sm={6} lg={4}>
                                            <AdvanceTableSearchBox className={`${UpliftCss.filterFieldBoxes}`} table placeholder={t("Search")} />
                                        </Col>
                                    </Row>
                                    <AdvanceTable
                                        table
                                        headerClassName="bg-200 text-900 text-nowrap align-middle"
                                        rowClassName="align-middle white-space-nowrap"
                                        tableProps={{
                                            bordered: true,
                                            striped: true,
                                            className: "fs--1 mb-0 overflow-hidden",
                                        }}
                                    />
                                    <div className="mt-3">
                                        <AdvanceTableFooter rowCount={companies.length} table rowInfo navButtons rowsPerPageSelection />
                                    </div>
                                </AdvanceTableWrapper>

                            </div>
                        </div>
                    </div>
                    {isFinish && <LoaderIcon />}
                    <Modal isOpen={modal} backdrop={false} keyboard={true}>
                        <ModalHeader toggle={toggle}>{t(em.delete_icon)} !!</ModalHeader>
                        <ModalBody>
                            {t(em.areyousuredelete)}
                            <br />
                            {companyData?.entityName}
                            <br />
                            <span style={{ color: "red", fontWeight: "bold" }}>{t(existMessage)}</span>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="primary" onClick={() => handleDelete()}>
                                {t(em.delete_icon)}
                            </Button>
                            <Button color="secondary" onClick={toggle}>
                                {t(em.Cancel)}
                            </Button>
                        </ModalFooter>
                    </Modal>

                    {loading && <div className="text-center mt-3">{t("Loading...")}</div>}
                </div>
            )}
        </Translation>
    );
};

export default ManageBranches;
